import { useCallback, useEffect, useRef, useState } from "react";
import { SUBJECTS } from "../lib/taxonomy";
import type { ComposerDraft, ComposerDraftState } from "./useComposerDraft";

const storageKey = (uid: string) => `@schoolme/web-text-draft/v1/${uid}`;

export async function clearComposerDraft(uid: string): Promise<boolean> {
  try { sessionStorage.removeItem(storageKey(uid)); return true; }
  catch { return false; }
}

export function useComposerDraft(uid: string | undefined, draft: ComposerDraft, restore: (draft: ComposerDraft) => void): ComposerDraftState {
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const draftRef = useRef(draft);
  const restoreRef = useRef(restore);
  const persistenceSuppressedRef = useRef(false);
  draftRef.current = draft;
  restoreRef.current = restore;
  const key = uid ? storageKey(uid) : null;
  useEffect(() => {
    if (!key) return;
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const value = JSON.parse(raw) as Partial<ComposerDraft>;
        if (typeof value.text !== "string" || (value.groupId !== null && typeof value.groupId !== "string")
          || typeof value.jobId !== "string" || typeof value.postId !== "string"
          || !SUBJECTS.some((subject) => subject.value === value.subject)
          || !["global", "group"].includes(value.audience ?? "")) throw new Error("Invalid draft");
        restoreRef.current(value as ComposerDraft);
      }
      setLoadedKey(key);
    } catch {
      setError("Brouillon non sauvegardé : le stockage du navigateur est indisponible. Copie ton texte avant de quitter cette page.");
    }
  }, [key]);
  useEffect(() => {
    if (!key || loadedKey !== key) return;
    try {
      if (persistenceSuppressedRef.current) {
        if (!draft.text) return;
        persistenceSuppressedRef.current = false;
      }
      if (draft.text) sessionStorage.setItem(key, JSON.stringify(draft));
      else sessionStorage.removeItem(key);
      setError(null);
    } catch {
      setError("Brouillon non sauvegardé : copie ton texte avant de quitter cette page.");
    }
  }, [draft, key, loadedKey]);
  useEffect(() => {
    if (!draft.text) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [draft.text]);

  const saveNow = useCallback(async () => {
    if (!key) return false;
    try {
      sessionStorage.setItem(key, JSON.stringify(draftRef.current));
      setError(null);
      return true;
    } catch {
      setError("Brouillon non sauvegardé : copie ton texte avant de quitter cette page.");
      return false;
    }
  }, [key]);

  const clearNow = useCallback(async () => {
    if (!key) return true;
    persistenceSuppressedRef.current = true;
    try { sessionStorage.removeItem(key); return true; }
    catch {
      setError("Brouillon non sauvegardé : le stockage du navigateur est indisponible.");
      return false;
    }
  }, [key]);

  return { error, loaded: !key || loadedKey === key, saveNow, clearNow };
}
